import {
  ApolloClient,
  InMemoryCache,
  createHttpLink,
  from,
  ApolloLink,
  Observable,
  type FetchResult,
} from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { GRAPHQL_URI } from "../config";
import { getTenantSubdomain } from "../utils/subdomain";
import { loaderLink } from "./loaderLink";
import {
  authCorrelationHeaders,
  createRequestId,
  locationMapCorrelationHeaders,
  recordAuthPageAction,
  startTimer,
} from "../utils/perfTelemetry";

const LOCATION_MAP_OPERATIONS = new Set([
  "LocationMapTree",
  "LocationMapDetail",
  "FloorPlan2DAssets",
  "MasterLocationTypes",
  "Locations",
]);

const locationMapLink = setContext((operation, { headers }) => {
  if (!LOCATION_MAP_OPERATIONS.has(operation.operationName || "")) {
    return { headers };
  }
  return {
    headers: {
      ...headers,
      ...locationMapCorrelationHeaders(),
    },
  };
});

/* =========================================================
   HTTP LINK
========================================================= */
const httpLink = createHttpLink({
  uri: GRAPHQL_URI,
});


/* =========================================================
   TOKEN REFRESH MANAGEMENT (Queue Based)
========================================================= */

let isRefreshing = false;
let pendingRequests: Array<(token: string | null) => void> = [];

const resolvePendingRequests = (token: string | null) => {
  pendingRequests.forEach((callback) => callback(token));
  pendingRequests = [];
};

const addPendingRequest = (callback: (token: string | null) => void) => {
  pendingRequests.push(callback);
};

const logout = () => {
  localStorage.clear();
  window.location.href = "/login";
};

/* =========================================================
   REFRESH TOKEN CALL
========================================================= */

const getNewAccessToken = async (): Promise<string | null> => {
  const refreshToken = localStorage.getItem("refreshToken");
  if (!refreshToken) return null;

  const requestId = createRequestId();
  const elapsed = startTimer();
  const queueDepth = pendingRequests.length;
  try {
    const response = await fetch(GRAPHQL_URI, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...authCorrelationHeaders(requestId),
      },
      body: JSON.stringify({
        query: `
          mutation RefreshToken($refreshToken: String!) {
            refreshToken(refreshToken: $refreshToken) {
              accessToken
              refreshToken
            }
          }
        `,
        variables: { refreshToken },
      }),
    });

    const result = await response.json();

    if (result.errors) {
      // Server explicitly rejected the refresh token — it is expired or invalid.
      // Return a special sentinel to signal callers to logout.
      console.error("Refresh token rejected by server:", result.errors);
      recordAuthPageAction({
        action: "refresh_token",
        outcome: "failure",
        duration_ms: elapsed(),
        requestId,
        failure_stage: "refresh_rejected",
        queue_depth: queueDepth,
        status: 401,
      });
      return "__EXPIRED__";
    }

    const newAccessToken = result.data?.refreshToken?.accessToken;
    const newRefreshToken = result.data?.refreshToken?.refreshToken;

    if (!newAccessToken) {
      recordAuthPageAction({
        action: "refresh_token",
        outcome: "failure",
        duration_ms: elapsed(),
        requestId,
        failure_stage: "missing_access_token",
        queue_depth: queueDepth,
        status: 401,
      });
      return null;
    }

    localStorage.setItem("accessToken", newAccessToken);
    if (newRefreshToken) {
      localStorage.setItem("refreshToken", newRefreshToken);
    }

    recordAuthPageAction({
      action: "refresh_token",
      outcome: "success",
      duration_ms: elapsed(),
      requestId,
      queue_depth: queueDepth,
      status: 200,
    });
    return newAccessToken;
  } catch (error) {
    console.error("Refresh token error:", error);
    recordAuthPageAction({
      action: "refresh_token",
      outcome: "failure",
      duration_ms: elapsed(),
      requestId,
      failure_stage: "network",
      queue_depth: queueDepth,
      status: 0,
    });
    return null;
  }
};

/* =========================================================
   AUTH LINK (Attach Token To Requests)
========================================================= */

const authLink = setContext((_, { headers }) => {
  const token = localStorage.getItem("accessToken");
  const tenantSlug = getTenantSubdomain();
  const existingRequestId =
    typeof headers?.["x-request-id"] === "string"
      ? headers["x-request-id"]
      : typeof headers?.["X-Request-ID"] === "string"
        ? headers["X-Request-ID"]
        : undefined;
  return {
    headers: {
      ...headers,
      authorization: token ? `Bearer ${token}` : "",
      ...(tenantSlug ? { "x-tenant-slug": tenantSlug } : {}),
      ...authCorrelationHeaders(existingRequestId),
    },
  };
});

/* =========================================================
   AUTH ERROR DETECTION
========================================================= */

const hasAuthError = (errors: readonly any[]): boolean => {
  if (!errors?.length) return false;
  return errors.some((err) => {
    const message = err.message?.toLowerCase() || "";
    const code = (err.extensions?.code as string) || (err.code as string) || "";
    return (
      message.includes("not authenticated") ||
      message.includes("unauthorized") ||
      code === "UNAUTHENTICATED" ||
      code === "401"
    );
  });
};

/* =========================================================
   ERROR LINK (Auto Refresh + Retry Failed Requests)
========================================================= */

const errorLink = new ApolloLink((operation, forward) => {
  return new Observable<FetchResult>((observer: {
    next: (value: FetchResult) => void;
    error: (err: unknown) => void;
    complete: () => void;
  }) => {
    let handle: any;
    let isRetrying = false; // Track if we're in a refresh+retry cycle

    const retryWithToken = (newToken: string) => {
      operation.setContext(({ headers = {} }: any) => ({
        headers: { ...headers, authorization: `Bearer ${newToken}` },
      }));
      forward(operation).subscribe({
        next: (r: FetchResult) => observer.next(r),
        error: (e: unknown) => observer.error(e),
        complete: () => observer.complete(),
      });
    };

    const handleAuthFailure = () => {
      logout();
      observer.error(new Error("Session expired"));
    };

    const doTokenRefresh = () => {
      isRetrying = true;

      if (isRefreshing) {
        // Another request is already refreshing — queue this one
        addPendingRequest((newToken) => {
          if (!newToken) {
            handleAuthFailure();
          } else {
            retryWithToken(newToken);
          }
        });
        return;
      }

      isRefreshing = true;
      getNewAccessToken()
        .then((newToken) => {
          isRefreshing = false;

          if (!newToken || newToken === "__EXPIRED__") {
            // No token or refresh token itself is expired — must logout
            resolvePendingRequests(null);
            handleAuthFailure();
            return;
          }

          resolvePendingRequests(newToken);
          retryWithToken(newToken);
        })
        .catch(() => {
          isRefreshing = false;
          resolvePendingRequests(null);
          handleAuthFailure();
        });
    };

    Promise.resolve(operation)
      .then(() => {
        const skipAuthRefresh = Boolean(operation.getContext()?.skipAuthRefresh);
        handle = forward(operation).subscribe({
          next: (result: FetchResult) => {
            if (
              !skipAuthRefresh &&
              hasAuthError((result.errors as any[]) || [])
            ) {
              // Auth error — kick off refresh+retry (do NOT call observer.next/complete here)
              doTokenRefresh();
              return;
            }
            // Normal result — pass through
            observer.next(result);
          },
          error: (networkErr: unknown) => {
            const statusCode =
              (networkErr as any)?.statusCode ||
              (networkErr as any)?.status;

            if (!skipAuthRefresh && statusCode === 401) {
              doTokenRefresh();
            } else {
              observer.error(networkErr);
            }
          },
          // Only complete if we are NOT retrying — otherwise the retried
          // subscription will call observer.complete() when it finishes.
          complete: () => {
            if (!isRetrying) {
              observer.complete();
            }
          },
        });
      })
      .catch(observer.error.bind(observer));

    return () => handle?.unsubscribe();
  });
});

/* =========================================================
   APOLLO CLIENT INSTANCE
========================================================= */

const apolloClient = new ApolloClient({
  link: from([loaderLink, errorLink, locationMapLink, authLink, httpLink]),
  cache: new InMemoryCache(),
  defaultOptions: {
    watchQuery: {
      errorPolicy: "all",
      fetchPolicy: "cache-first",
    },
    query: {
      errorPolicy: "all",
      fetchPolicy: "cache-first",
    },
  } as any,
});

export default apolloClient;
